import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"

import { format } from "date-fns"
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  MapPin,
  MoreHorizontal,
  RouteIcon,
  XCircle,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useCarParks } from "@/hooks/useCarParks"
import { useDepots } from "@/hooks/useDepots"
import { type ShiftWithDisplay, useShiftDialog } from "@/hooks/useShiftDialog"
import { useShifts } from "@/hooks/useShifts"
import { ukDayEndOf, ukDayStartOf } from "@/lib/ukCalendarDate"
import type { Shift } from "@/services/shiftsService"
import { Person } from "@/types/person"
import { exportShiftsToCSV } from "@/utils/csvExportUtils"
import { formatDuration } from "@/utils/durationFormat"
import { getShiftDisplayStatus } from "@/utils/shiftDisplayStatus"
import { formatTo24HourTime } from "@/utils/timeFormat"

import ShiftDetailsDialog from "../shift/ShiftDetailsDialog"
import { getShiftTypeIcon } from "../ui/shiftTypeIcons"
import { DailyScheduleSkeleton } from "./SkeletonLoaders"
import { UNKNOWN_DEPOT, formatClockInLocation } from "./clockInLocation"

interface DailyScheduleViewProps {
  date: Date
  scheduleType: "delivery" | "collection" | "sortation" | "internal"
  filters: {
    location: string
    employer: string
    type: string
    areas: string[]
  }
  searchTerm: string
  onCountChange?: (count: number) => void
  people: Person[]
  peopleLoading: boolean
}

export interface DailyScheduleViewRef {
  exportToCSV: () => void
}

const DailyScheduleView = forwardRef<DailyScheduleViewRef, DailyScheduleViewProps>(
  (
    {
      date,
      scheduleType: _scheduleType,
      filters,
      searchTerm,
      onCountChange,
      people,
      peopleLoading,
    },
    ref,
  ) => {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

    // Use shared shift dialog hook
    const {
      shiftDetailsOpen,
      setShiftDetailsOpen,
      selectedShift,
      detailsMode,
      isSaving,
      handleShiftClick,
      handleShiftSave,
      handleForceClockIn,
      handleForceClockOut,
    } = useShiftDialog()
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

    // Build function request for the selected day
    const dayStart = useMemo(() => ukDayStartOf(date).toISOString(), [date])
    const dayEnd = useMemo(() => ukDayEndOf(date).toISOString(), [date])

    const {
      data: shifts = [],
      isLoading,
      isFetching,
    } = useShifts({
      startTime: dayStart,
      endTime: dayEnd,
      location: filters.location || undefined,
      employer: filters.employer !== "all" ? filters.employer : undefined,
      type: filters.type !== "all" ? filters.type : undefined,
      areas: filters.areas && filters.areas.length ? filters.areas : undefined,
      page: 1,
      pageSize: 500,
    })
    const { data: depots = [], isLoading: depotsLoading } = useDepots()
    const { data: carParks = [], isLoading: carParksLoading } = useCarParks()
    // Until both lists arrive, a lookup miss would read as "Unknown depot"
    const clockLocationsReady = !depotsLoading && !carParksLoading
    const depotNameById = useMemo(() => {
      const map = new Map<string, string>()
      depots.forEach((depot) => {
        map.set(depot.id, depot.name)
      })
      return map
    }, [depots])
    const carParkById = useMemo(
      () => new Map(carParks.map((carPark) => [carPark.id, carPark])),
      [carParks],
    )
    const personNameById = useMemo(() => {
      const map = new Map<string, string>()
      people.forEach((person) => {
        map.set(person.id, person.person)
      })
      return map
    }, [people])

    // Track if we've loaded at least once
    useEffect(() => {
      if (!isLoading && shifts.length >= 0) {
        setHasLoadedOnce(true)
      }
    }, [isLoading, shifts.length])

    // Notify parent about fetched count
    useEffect(() => {
      onCountChange?.(shifts.length)
    }, [shifts.length, onCountChange])

    // Local toggles for visibility controls in Day view
    const [hideEmpty, setHideEmpty] = useState(false)
    const [hideCancelled, setHideCancelled] = useState(false)

    // Add display status to shifts without mapping
    const shiftsWithDisplay: ShiftWithDisplay[] = useMemo(() => {
      return shifts
        .map((s) => ({
          ...s,
          displayStatus: getShiftDisplayStatus(s),
        }))
        .sort((a, b) => {
          // First sort by start time
          const timeCompare = a.startTime.localeCompare(b.startTime)
          if (timeCompare !== 0) return timeCompare
          // Then sort by person name
          const aName = a.people?.person || "Unassigned"
          const bName = b.people?.person || "Unassigned"
          return aName.localeCompare(bName)
        })
    }, [shifts])

    const toggleSection = (jobType: string) => {
      setExpandedSections((prev) => ({
        ...prev,
        [jobType]: !prev[jobType],
      }))
    }

    // allShifts is sourced from server

    const filteredShifts = useMemo(() => {
      return shiftsWithDisplay
        .filter((shift) => {
          const personName = shift.people?.person || "Unassigned"
          const matchesSearch =
            personName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shift.id.includes(searchTerm)
          const matchesLocation = shift.location
            .toLowerCase()
            .includes(filters.location.toLowerCase())
          const matchesEmployer =
            filters.employer === "all" ||
            shift.employer.toLowerCase().includes(filters.employer.toLowerCase())
          const matchesArea =
            !filters.areas ||
            filters.areas.length === 0 ||
            filters.areas.some((a) => a.toLowerCase() === (shift.area || "").toLowerCase())
          const matchesType =
            filters.type === "all" || shift.type.toLowerCase() === filters.type.toLowerCase()
          const hideBecauseEmpty =
            hideEmpty && (shift.displayStatus === "empty" || personName === "Unassigned")
          const hideBecauseCancelled =
            hideCancelled &&
            (shift.displayStatus === "cancelled" || shift.displayStatus === "unnotified-absence")

          return (
            matchesSearch &&
            matchesLocation &&
            matchesEmployer &&
            matchesArea &&
            matchesType &&
            !hideBecauseEmpty &&
            !hideBecauseCancelled
          )
        })
        .sort((a, b) => {
          // First, separate cancelled/unnotified-absence shifts to bottom
          const aIsCancelled =
            a.displayStatus === "cancelled" || a.displayStatus === "unnotified-absence"
          const bIsCancelled =
            b.displayStatus === "cancelled" || b.displayStatus === "unnotified-absence"

          // If one is cancelled and other isn't, put cancelled last
          if (aIsCancelled && !bIsCancelled) return 1
          if (!aIsCancelled && bIsCancelled) return -1

          // If both are same category, sort by time
          return a.startTime.localeCompare(b.startTime)
        })
    }, [shiftsWithDisplay, searchTerm, filters, hideEmpty, hideCancelled])

    // Expose export function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        exportToCSV: () => {
          // Convert ShiftWithDisplay back to Shift for export
          const shiftsToExport: Shift[] = filteredShifts.map((shift) => ({
            id: shift.id,
            peopleId: shift.peopleId,
            startTime: shift.startTime,
            endTime: shift.endTime,
            minutesLate: shift.minutesLate,
            awol: shift.awol,
            status: shift.status,
            area: shift.area,
            type: shift.type,
            employer: shift.employer,
            location: shift.location,
            depotId: shift.depotId,
            vehicleReg: shift.vehicle?.reg ?? null,
            notes: shift.notes,
            preassignedRoute: shift.preassignedRoute,
            cancelledReason: shift.cancelledReason,
            cancelledTime: shift.cancelledTime,
            notifications: shift.notifications,
            lastEditedBy: shift.lastEditedBy,
            people: shift.people,
            timesheet: shift.timesheet,
          }))
          const filename = `shifts_daily_${format(date, "yyyy-MM-dd")}_${shiftsToExport.length}.csv`
          exportShiftsToCSV(shiftsToExport, filename)
        },
      }),
      [filteredShifts, date],
    )

    // Group shifts by area with filled/total counts
    const groupedShifts = useMemo(() => {
      const groups: {
        [key: string]: { shifts: ShiftWithDisplay[]; filled: number; total: number }
      } = {}

      // Calculate filled/total from shiftsWithDisplay with location/role/type/areas filters + hide toggles
      // Do NOT apply search filter to counts
      const shiftsForCounts = shiftsWithDisplay.filter((shift) => {
        const personName = shift.people?.person || "Unassigned"
        const matchesLocation = shift.location
          .toLowerCase()
          .includes(filters.location.toLowerCase())
        const matchesEmployer =
          filters.employer === "all" ||
          shift.employer.toLowerCase().includes(filters.employer.toLowerCase())
        const matchesArea =
          !filters.areas ||
          filters.areas.length === 0 ||
          filters.areas.some((a) => a.toLowerCase() === (shift.area || "").toLowerCase())
        const matchesType =
          filters.type === "all" || shift.type.toLowerCase() === filters.type.toLowerCase()
        const hideBecauseEmpty =
          hideEmpty && (shift.displayStatus === "empty" || personName === "Unassigned")
        const hideBecauseCancelled =
          hideCancelled &&
          (shift.displayStatus === "cancelled" || shift.displayStatus === "unnotified-absence")

        return (
          matchesLocation &&
          matchesEmployer &&
          matchesArea &&
          matchesType &&
          !hideBecauseEmpty &&
          !hideBecauseCancelled
        )
      })

      // Calculate filled/total counts from shiftsForCounts
      const areaCounts: {
        [key: string]: { filled: number; total: number }
      } = {}

      shiftsForCounts.forEach((shift) => {
        const key = shift.area || "Unknown"
        if (!areaCounts[key]) {
          areaCounts[key] = { filled: 0, total: 0 }
        }
        areaCounts[key].total++
        // Count as filled if not empty or cancelled
        if (
          shift.displayStatus !== "empty" &&
          shift.displayStatus !== "open" &&
          shift.displayStatus !== "cancelled"
        ) {
          areaCounts[key].filled++
        }
      })

      // Now group the filtered shifts (which includes search and hide toggles)
      filteredShifts.forEach((shift) => {
        const key = shift.area || "Unknown"
        if (!groups[key]) {
          groups[key] = {
            shifts: [],
            filled: areaCounts[key]?.filled || 0,
            total: areaCounts[key]?.total || 0,
          }
        }
        groups[key].shifts.push(shift)
      })

      return groups
    }, [filteredShifts, shiftsWithDisplay, filters, hideEmpty, hideCancelled])

    // Expand sections by default only when group keys change or new keys appear
    useEffect(() => {
      const jobTypes = Object.keys(groupedShifts)
      const currentKeys = Object.keys(expandedSections)
      const keysChanged =
        jobTypes.length !== currentKeys.length || jobTypes.some((k) => !(k in expandedSections))

      if (keysChanged) {
        const next: { [key: string]: boolean } = {}
        jobTypes.forEach((k) => {
          next[k] = expandedSections[k] ?? true
        })
        setExpandedSections(next)
      }
    }, [groupedShifts, expandedSections])

    // Note: Pagination removed as we now show all shifts grouped by job type

    const getTypeIcon = (type: string) => {
      const TypeIcon = getShiftTypeIcon(type)
      return <TypeIcon className="h-4 w-4" />
    }

    const getStatusColor = (status: ShiftWithDisplay["displayStatus"]) => {
      switch (status) {
        case "planned":
          return "bg-white border border-palette-jungleGreen200 text-palette-jungleGreen800"
        case "in-progress":
          return "bg-green-100 border border-green-300 text-green-800"
        case "empty":
          return "bg-blue-100 text-blue-800"
        case "open":
          return "bg-palette-jungleGreen100 border border-palette-jungleGreen200 text-palette-jungleGreen800"
        case "clocked-in":
          return "bg-green-300 border border-green-500 text-green-900"
        case "clocked-out":
          return "bg-green-50 border border-green-200 text-green-800"
        case "overdue":
          return "bg-orange-100 border border-orange-300 text-orange-800"
        case "no-show":
          return "bg-red-100 border border-red-300 text-red-800"
        case "cancelled":
          return "bg-purple-100 border border-purple-300 text-purple-800"
        case "unnotified-absence":
          return "bg-amber-100 border border-amber-300 text-amber-800"
        default:
          return "bg-white border border-palette-jungleGreen200 text-palette-jungleGreen800"
      }
    }

    const getKeyColor = (status: ShiftWithDisplay["displayStatus"]) => {
      switch (status) {
        case "planned":
          return "bg-white border border-palette-jungleGreen200"
        case "in-progress":
          return "bg-green-100 border border-green-300"
        case "empty":
          return "bg-blue-100 border border-blue-200"
        case "open":
          return "bg-palette-jungleGreen100 border border-palette-jungleGreen200"
        case "clocked-in":
          return "bg-green-300 border border-green-500"
        case "clocked-out":
          return "bg-green-50 border border-green-200"
        case "overdue":
          return "bg-orange-100 border border-orange-300"
        case "no-show":
          return "bg-red-100 border border-red-300"
        case "cancelled":
          return "bg-purple-100 border border-purple-300"
        case "unnotified-absence":
          return "bg-amber-100 border border-amber-300"
        default:
          return "bg-palette-jungleGreen200"
      }
    }

    const getStatusIcon = (status: ShiftWithDisplay["displayStatus"]) => {
      switch (status) {
        case "planned":
          return <Circle className="h-4 w-4 text-palette-jungleGreen500" />
        case "in-progress":
          return <Clock className="h-4 w-4 text-green-600" />
        case "empty":
          return <AlertCircle className="h-4 w-4 text-blue-600" />
        case "open":
          return <Circle className="h-4 w-4 text-palette-jungleGreen400" />
        case "clocked-in":
          return <CheckCircle className="h-4 w-4 text-green-600" />
        case "clocked-out":
          return <CheckCircle className="h-4 w-4 text-green-600" />
        case "overdue":
          return <AlertTriangle className="h-4 w-4 text-orange-600" />
        case "no-show":
          return <AlertTriangle className="h-4 w-4 text-red-600" />
        case "cancelled":
          return <XCircle className="h-4 w-4 text-purple-600" />
        case "unnotified-absence":
          return <XCircle className="h-4 w-4 text-amber-600" />
        default:
          return <Circle className="h-4 w-4 text-palette-jungleGreen500" />
      }
    }

    const getStatusText = (status: ShiftWithDisplay["displayStatus"]) => {
      switch (status) {
        case "planned":
          return "Planned"
        case "in-progress":
          return "In Progress"
        case "empty":
          return "Unfulfilled"
        case "open":
          return "Open"
        case "clocked-in":
          return "Clocked In"
        case "clocked-out":
          return "Completed"
        case "overdue":
          return "Overdue"
        case "no-show":
          return "No Show"
        case "cancelled":
          return "Cancelled"
        case "unnotified-absence":
          return "Unnotified Absence"
        default:
          return "Unknown"
      }
    }

    const getShiftTypeCounts = () => {
      // Use shiftsWithDisplay but apply location, employer, type, areas filters + hide toggles
      // Do NOT apply search filter to status cards
      const shiftsForCounts = shiftsWithDisplay.filter((shift) => {
        const personName = shift.people?.person || "Unassigned"
        const matchesLocation = shift.location
          .toLowerCase()
          .includes(filters.location.toLowerCase())
        const matchesEmployer =
          filters.employer === "all" ||
          shift.employer.toLowerCase().includes(filters.employer.toLowerCase())
        const matchesArea =
          !filters.areas ||
          filters.areas.length === 0 ||
          filters.areas.some((a) => a.toLowerCase() === (shift.area || "").toLowerCase())
        const matchesType =
          filters.type === "all" || shift.type.toLowerCase() === filters.type.toLowerCase()
        const hideBecauseEmpty =
          hideEmpty && (shift.displayStatus === "empty" || personName === "Unassigned")
        const hideBecauseCancelled =
          hideCancelled &&
          (shift.displayStatus === "cancelled" || shift.displayStatus === "unnotified-absence")

        return (
          matchesLocation &&
          matchesEmployer &&
          matchesArea &&
          matchesType &&
          !hideBecauseEmpty &&
          !hideBecauseCancelled
        )
      })

      const counts = shiftsForCounts.reduce(
        (acc, shift) => {
          acc[shift.displayStatus] = (acc[shift.displayStatus] || 0) + 1
          return acc
        },
        {} as Record<string, number>,
      )
      return counts
    }

    const getOrderedStatusCounts = () => {
      const counts = getShiftTypeCounts()
      const statusOrder = [
        "planned",
        "open",
        "empty",
        "in-progress",
        "clocked-out",
        "overdue",
        "no-show",
        "cancelled",
        "unnotified-absence",
      ]

      return statusOrder.map((status) => [status, counts[status] || 0] as [string, number])
    }

    const orderedStatusCounts = getOrderedStatusCounts()

    // Show skeleton loader on initial load
    if (isLoading && !hasLoadedOnce) {
      return (
        <TooltipProvider>
          <DailyScheduleSkeleton />
        </TooltipProvider>
      )
    }

    return (
      <TooltipProvider>
        <div className="space-y-4">
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-4 w-full">
            {orderedStatusCounts.map(([status, count]) => (
              <div
                key={status}
                className="bg-palette-jungleGreen000 rounded-lg p-3 flex flex-col items-center justify-between min-h-[5rem]"
              >
                <div className="flex items-start space-x-2">
                  <div
                    className={`w-3 h-3 shrink-0 rounded mt-0.5 ${getKeyColor(
                      status as ShiftWithDisplay["displayStatus"],
                    )}`}
                  ></div>
                  <span className="font-medium text-sm leading-none">
                    {getStatusText(status as ShiftWithDisplay["displayStatus"])}
                  </span>
                </div>
                <div className="text-2xl font-bold text-palette-jungleGreen900 text-center">
                  {count}
                </div>
              </div>
            ))}
          </div>

          {/* Visibility Controls (Day View) */}
          <div className="flex items-center justify-between gap-6 bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded-md px-3 py-2">
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-palette-jungleGreen700">
                <Switch checked={hideEmpty} onCheckedChange={setHideEmpty} />
                Hide Empty
              </label>
              <label className="flex items-center gap-2 text-sm text-palette-jungleGreen700">
                <Switch checked={hideCancelled} onCheckedChange={setHideCancelled} />
                Hide Cancelled
              </label>
            </div>
            {isFetching && hasLoadedOnce && (
              <div className="flex items-center gap-2 text-sm text-palette-jungleGreen600">
                <Spinner size="sm" />
                <span>Updating list...</span>
              </div>
            )}
          </div>

          {/* Shifts List - Grouped by Job Type */}
          <div className="space-y-6">
            {Object.entries(groupedShifts).map(([area, groupData]) => (
              <div key={area} className="border rounded-lg overflow-hidden">
                {/* Area Header - Matching ListView style */}
                <div
                  className="bg-palette-jungleGreen000 px-4 py-3 border-b cursor-pointer hover:bg-palette-jungleGreen100 transition-colors"
                  onClick={() => toggleSection(area)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {expandedSections[area] ? (
                        <ChevronDown className="h-5 w-5 text-palette-jungleGreen600" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-palette-jungleGreen600" />
                      )}
                      <h3 className="font-semibold text-palette-jungleGreen900">
                        {area} ({groupData.total})
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="text-xs">
                        {groupData.filled}/{groupData.total}
                      </Badge>
                      {getTypeIcon(area)}
                    </div>
                  </div>
                </div>

                {/* Shifts for this job type - Conditionally rendered */}
                {expandedSections[area] && (
                  <div className="space-y-2 p-2">
                    {groupData.shifts.map((shift) => {
                      const personName = shift.people?.person || "Unassigned"
                      const primaryPersonId = shift.people?.primaryPersonId ?? null
                      const primaryDriverName = primaryPersonId
                        ? (personNameById.get(primaryPersonId) ?? null)
                        : null
                      const startTimeFormatted = formatTo24HourTime(shift.startTime)!
                      const endTimeFormatted = formatTo24HourTime(shift.endTime)!
                      const clockInTimeFormatted = formatTo24HourTime(
                        shift.timesheet?.clockedInTime,
                      )
                      const clockOutTimeFormatted = formatTo24HourTime(
                        shift.timesheet?.clockedOutTime,
                      )
                      const clockInLocation =
                        clockLocationsReady && shift.timesheet?.clockinDepotId
                          ? formatClockInLocation(
                              depotNameById.get(shift.timesheet.clockinDepotId) ?? null,
                              carParkById.get(shift.timesheet.clockinCarParkId) ?? null,
                            )
                          : null
                      const clockOutLocation =
                        clockLocationsReady && shift.timesheet?.clockoutDepotId
                          ? (depotNameById.get(shift.timesheet.clockoutDepotId) ?? UNKNOWN_DEPOT)
                          : null
                      const plannedDuration = formatDuration(shift.startTime, shift.endTime)
                      const actualDuration = formatDuration(
                        shift.timesheet?.clockedInTime,
                        shift.timesheet?.clockedOutTime,
                      )

                      return (
                        <div
                          key={shift.id}
                          className={`p-4 rounded-lg border-l-4 ${getStatusColor(
                            shift.displayStatus,
                          )} hover:shadow-sm transition-shadow cursor-pointer`}
                          onClick={() => {
                            handleShiftClick(shift)
                          }}
                        >
                          <div className="flex items-start justify-between">
                            {/* Left side - Type and role badges */}
                            <div className="flex items-start space-x-3">
                              <div className="flex flex-col space-y-1 pt-1 w-48">
                                <Badge className="bg-green-100 text-green-800 border-green-200 text-xs w-fit hover:bg-green-100">
                                  {getTypeIcon(shift.type)}
                                  <span className="ml-1">{shift.type}</span>
                                </Badge>
                                <Badge variant="outline" className="text-xs w-fit">
                                  {shift.employer}
                                </Badge>
                              </div>

                              {/* Main content area */}
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div className="font-bold text-lg text-palette-jungleGreen900">
                                    {personName}
                                  </div>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>{getStatusIcon(shift.displayStatus)}</div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{getStatusText(shift.displayStatus)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                {primaryPersonId && (
                                  <div className="text-xs text-palette-jungleGreen500">
                                    Primary: {primaryDriverName ?? "Unknown"}
                                  </div>
                                )}
                                <div className="flex items-start gap-4 mt-1">
                                  {/* Planned times - prominently displayed */}
                                  <div className="mt-1 flex items-center space-x-1 text-palette-jungleGreen600">
                                    <Clock className="h-4 w-4" />
                                    <span className="font-medium whitespace-nowrap">
                                      {startTimeFormatted} - {endTimeFormatted}
                                    </span>
                                  </div>

                                  {/* Duration - Planned */}
                                  {plannedDuration && (
                                    <div className="text-sm text-palette-jungleGreen500">
                                      {plannedDuration}
                                    </div>
                                  )}

                                  {/* Actual times if available */}
                                  {(clockInTimeFormatted || clockOutTimeFormatted) && (
                                    <div className="mt-1 flex items-start gap-2 text-sm text-green-700">
                                      {clockInTimeFormatted && (
                                        <div className="space-y-0.5">
                                          <div className="flex items-center space-x-1 whitespace-nowrap">
                                            <Clock className="h-3 w-3" />
                                            <span>In: {clockInTimeFormatted}</span>
                                          </div>
                                          {clockInLocation && (
                                            <div className="flex items-start gap-1 text-xs leading-4">
                                              <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                                              <span className="whitespace-nowrap">
                                                {clockInLocation}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {clockOutTimeFormatted && (
                                        <div className="space-y-0.5">
                                          <div className="flex items-center space-x-1 whitespace-nowrap">
                                            <Clock className="h-3 w-3" />
                                            <span>Out: {clockOutTimeFormatted}</span>
                                          </div>
                                          {clockOutLocation && (
                                            <div className="flex items-start gap-1 text-xs leading-4">
                                              <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                                              <span className="whitespace-nowrap">
                                                {clockOutLocation}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {actualDuration && (
                                        <div className="text-green-700 font-medium">
                                          {actualDuration}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Right side - Notes, route, actions */}
                            <div className="flex items-start gap-2">
                              <div className="w-72 shrink-0 flex justify-start">
                                {shift.preassignedRoute && (
                                  <Badge
                                    variant="outline"
                                    className="bg-blue-100 text-blue-900 border-blue-300 text-xs max-w-full"
                                  >
                                    <RouteIcon className="h-4 w-4" />
                                    <span className="ml-1 shrink-0">Preassigned Route</span>
                                    <span className="ml-1 truncate">{shift.preassignedRoute}</span>
                                  </Badge>
                                )}
                              </div>
                              <div className="w-72 shrink-0 flex justify-end">
                                {shift.notes ? (
                                  <div className="text-sm text-palette-jungleGreen600 bg-palette-jungleGreen000 rounded px-2 py-1 w-full">
                                    {shift.notes}
                                  </div>
                                ) : (
                                  <div className="invisible text-sm text-palette-jungleGreen600 bg-palette-jungleGreen000 rounded px-2 py-1 truncate w-full">
                                    No notes
                                  </div>
                                )}
                              </div>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          <ShiftDetailsDialog
            mode={detailsMode}
            shift={selectedShift}
            open={shiftDetailsOpen}
            onOpenChange={setShiftDetailsOpen}
            onSave={handleShiftSave}
            onForceClockIn={handleForceClockIn}
            people={people}
            peopleLoading={peopleLoading}
            onForceClockOut={handleForceClockOut}
            isSaving={isSaving}
          />
        </div>
      </TooltipProvider>
    )
  },
)

DailyScheduleView.displayName = "DailyScheduleView"

export default DailyScheduleView
